<template>
  <div class="srail">
    <create ref="create" no-activator @reload="reload"></create>

    <!-- Desktop: vertical rail -->
    <div v-if="!$vuetify.breakpoint.smAndDown" class="srail__list">
      <div class="srow srow--create" @click="openCreate">
        <span class="srow__avatar srow__avatar--create">
          <hero-icon :icon="UserPlusIcon" :size="16" />
        </span>
        <div class="srow__main">
          <div class="srow__name srow__name--create">Nuevo miembro</div>
        </div>
      </div>

      <div
        v-for="employee in staff"
        :key="employee.id"
        class="srow"
        :class="{ 'srow--active': isActive(employee) }"
        @click="selected = employee"
        >
        <v-img
          v-if="employee.avatarUrl"
          :src="employee.avatarUrl"
          class="srow__avatar srow__avatar--img"
          ></v-img>
        <span v-else class="srow__avatar">{{ initial(employee) }}</span>

        <div class="srow__main">
          <div class="srow__name">{{ employee.name }}</div>
          <div class="srow__detail">
            <hero-icon :icon="BriefcaseIcon" :size="12" />
            <span>{{ subtitle(employee) }}</span>
          </div>
        </div>
      </div>

      <div v-if="loading" class="srail__state">Cargando…</div>
      <div v-else-if="!staff.length" class="srail__state">Sin personal</div>
    </div>

    <!-- Mobile: horizontal scroll of chips -->
    <div v-else class="schips">
      <div class="schip schip--create" @click="openCreate">
        <span class="schip__avatar schip__avatar--create">
          <hero-icon :icon="UserPlusIcon" :size="14" />
        </span>
        <span class="schip__name schip__name--create">Nuevo</span>
      </div>

      <div
        v-for="employee in staff"
        :key="employee.id"
        class="schip"
        :class="{ 'schip--active': isActive(employee) }"
        @click="selected = employee"
        >
        <v-img
          v-if="employee.avatarUrl"
          :src="employee.avatarUrl"
          class="schip__avatar schip__avatar--img"
          ></v-img>
        <span v-else class="schip__avatar">{{ initial(employee) }}</span>
        <span class="schip__name">{{ employee.name }}</span>
      </div>

      <div v-if="loading" class="srail__state schip-state">Cargando…</div>
      <div v-else-if="!staff.length" class="srail__state schip-state">Sin personal</div>
    </div>
  </div>
</template>

<script>
import Create from './Create'
import { Staff } from '@/graphql/queries/staff'
import HeroIcon from '@/components/shared/HeroIcon'
import BriefcaseIcon from 'heroicons/24/outline/briefcase.svg'
import UserPlusIcon from 'heroicons/24/outline/user-plus.svg'
import MapPinIcon from 'heroicons/24/outline/map-pin.svg'

export default {
  data: () => ({
    BriefcaseIcon,
    UserPlusIcon,
    MapPinIcon,
    staff: [],
    selected: null,
    loading: false
  }),

  created () {
    this.fetchStaff('cache-first')
  },

  watch: {
    selected () {
      this.$emit('select', this.selected)
    }
  },

  methods: {
    initial (employee) {
      const name = employee && employee.name && employee.name.trim()
      return name ? name.charAt(0).toUpperCase() : '?'
    },

    isActive (employee) {
      return !!this.selected && this.selected.id === employee.id
    },

    subtitle (employee) {
      const opLabels = { sell: 'Ventas', rent: 'Alquileres' }
      const role = employee.rol || opLabels[employee.operationType] || employee.operationType
      const parts = [role, employee.city].filter(Boolean)
      return parts.length ? parts.join(' · ') : 'Sin datos'
    },

    openCreate () {
      this.$refs.create.open()
    },

    reload () {
      this.fetchStaff('network-only')
    },

    fetchStaff (policy = 'cache-first') {
      this.loading = true
      const prevId = this.selected && this.selected.id

      this.$apollo.query({
        query: Staff,
        fetchPolicy: policy
      }).then(res => {
        this.staff = res.data.staff

        // Re-resolve the previously-selected member from the new array so the
        // highlight stays stable and Detail doesn't reset to staff[0] on reload.
        if (prevId) {
          const match = this.staff.find(e => e.id === prevId)
          this.selected = match || (this.staff.length ? this.staff[0] : null)
        } else if (!this.selected && this.staff.length) {
          // Auto-select first only when nothing is selected yet.
          this.selected = this.staff[0]
        }
      }).finally(() => {
        this.loading = false
      })
    }
  },

  components: {
    Create,
    HeroIcon
  }
}
</script>

<style scoped>
.srail {
  display: flex;
  flex-direction: column;
}

/* ---------- Desktop vertical rail ---------- */
.srail__list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

/* "Nuevo miembro" as the first row */
.srow--create {
  background: #fff;
  border: 1px dashed #e2cdc9;
  border-left-width: 1px;
  margin-bottom: 6px;
}
.srow--create:hover {
  background: #fdf4f3;
  border-color: #b52118;
}
.srow__avatar--create {
  background: #b52118;
  color: #fff;
}
.srow__name--create {
  color: #b52118;
}

.srow {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid transparent;
  border-left: 3px solid transparent;
  cursor: pointer;
  transition: background-color 0.18s ease, border-color 0.18s ease;
}
.srow:hover {
  background: #faf8f4;
  border-color: #dcd4c6;
}
.srow--active,
.srow--active:hover {
  background: rgba(117, 100, 84, 0.14);
  border-color: transparent;
  border-left-color: #b52118;
}

.srow__avatar {
  flex: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #1a1814;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
}
.srow__avatar--img { background: #ece7de; }

.srow__main { flex: 1; min-width: 0; }
.srow__name {
  font-size: 14px;
  font-weight: 600;
  color: #1a1814;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.srow__detail {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 2px;
  font-size: 12px;
  color: #756f64;
}
.srow__detail span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.srow__detail .hero-icon { color: #a39a8c; flex: none; }

.srail__state {
  font-size: 13px;
  color: #a39a8c;
  padding: 12px 4px;
}

/* ---------- Mobile horizontal chips ---------- */
.schips {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  scroll-snap-type: x proximity;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 4px;
}
.schips::-webkit-scrollbar { display: none; }
.schips { scrollbar-width: none; -ms-overflow-style: none; }

.schip {
  flex: none;
  scroll-snap-align: start;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px 6px 6px;
  border-radius: 999px;
  border: 1px solid #ece7de;
  background: #fff;
  cursor: pointer;
  transition: background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
}
.schip:hover { border-color: #dcd4c6; }
.schip--active {
  background: rgba(117, 100, 84, 0.14);
  border-color: #b52118;
  box-shadow: inset 0 0 0 1px #b52118;
}

/* "Nuevo" create chip (first chip) */
.schip--create { border-style: dashed; border-color: #e2cdc9; }
.schip--create:hover { background: #fdf4f3; border-color: #b52118; }
.schip__avatar--create { background: #b52118; color: #fff; }
.schip__name--create { color: #b52118; }

.schip__avatar {
  flex: none;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #1a1814;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
}
.schip__avatar--img { background: #ece7de; }

.schip__name {
  font-size: 13px;
  font-weight: 600;
  color: #1a1814;
  white-space: nowrap;
}

.schip-state {
  flex: none;
  align-self: center;
  padding: 8px 4px;
}
</style>
